import React, { useEffect, useState } from "react";
import { EngineViewModel, ModelInfoViewModel } from "../../api/modelInfoApi";
import { InputGroup, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ThreeDots } from "react-bootstrap-icons";
import ModelInfoInputFieldArray from "../inputfields/ModelInfoInputFieldArray";
import ModelInfoInputField from "../inputfields/ModelInfoInputField";
import { InfoCircle } from "react-bootstrap-icons";
import { HybrideTypeModel } from "../../api/hybridTypeApi";
import ModelInfoSelectObject from "../inputfields/ModelInfoSelectObject";
import { Vehicle } from "../../vehicleFiles/ModelInfos";
import ContextMenuEngine, {
  AvailableOptions,
  ContextMenuOption,
} from "../contextmenus/ContextMenuEngine";

interface ModelInfoEngineEditModalProps {
  vehicle: Vehicle;
  hybridTypes: HybrideTypeModel[];
  disabled: boolean;
  engineCell: any;
  engineKey: string;
  onContextMenu: (cell: any, key: string) => void;
  onCancel: (value: EngineViewModel) => void;
}

function ModelInfoEngineEditModal({
  vehicle,
  hybridTypes,
  disabled,
  engineCell,
  engineKey,
  onContextMenu,
  onCancel,
}: ModelInfoEngineEditModalProps) {
  const [show, setShow] = useState(true);
  const [engine, setEngine] = useState(vehicle.currentValue.engine);
  const [showAxles, setShowAxles] = useState<Boolean>(false);
  const [initialValueAxles, setInitialValueAxles] = useState(false);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [isContextMenuEnabled, setIsContextMenuEnabled] = useState(true);
  const [cellCopied, setCellCopied] = useState<any>(engineCell);
  const [contextMenuOptions, setContextMenuOptions] = useState<
    ContextMenuOption[]
  >([]);
  const [cellKey, setCellKey] = useState<string>(engineKey);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [contextMenuCell, setContextMenuCell] = useState<any>();

  useState<ModelInfoViewModel[]>();

  const updateEngine = (column: string, value: any) => {
    var updatedEngine = { ...engine };

    updatedEngine = {
      ...updatedEngine,
      [column]: value,
    };

    setEngine(updatedEngine);
  };

  useEffect(() => {
    if (
      engine.engineFrontAxle1MaximumKws.length !== 0 ||
      engine.engineFrontAxle1NominalKws.length !== 0 ||
      engine.engineFrontAxle2MaximumKws.length !== 0 ||
      engine.engineFrontAxle2NominalKws.length !== 0 ||
      engine.engineRearAxle1MaximumKws.length !== 0 ||
      engine.engineRearAxle1NominalKws.length !== 0 ||
      engine.engineRearAxle2MaximumKws.length !== 0 ||
      engine.engineRearAxle2NominalKws.length !== 0
    ) {
      setShowAxles(true);
      setInitialValueAxles(true);
    }
  }, [engine]);

  const handleClose = () => {
    setShow(false);
    onCancel(engine);

    onContextMenu(cellCopied, cellKey);
  };

  const toggleAxles = () => {
    setShowAxles(!showAxles);
    setInitialValueAxles(false);
  };

  const handleContextMenu = (
    event: React.MouseEvent<HTMLDivElement>,
    cell: any = false,
    key: string = "",
  ) => {
    const target = event.target as typeof event.target & Element;
    // don't trigger on inputs (they have their own context menu)
    if (target.nodeName === "INPUT") {
      setIsContextMenuVisible(false);
      return;
    }
    event.preventDefault();

    const position = {
      top: event.clientY,
      left: event.clientX,
    };

    setContextMenuPosition(position);
    setIsContextMenuVisible(true);
    setCellKey(key);

    if (cell !== false && (cell.length > 0 || cell !== undefined)) {
      setContextMenuCell(cell);
    }

    let options: ContextMenuOption[] = [];

    if (!disabled) {
      if (
        cell !== false &&
        cell !== "[None]" &&
        (cell.length > 0 || (cell !== undefined && cell.length === undefined))
      ) {
        options.push(AvailableOptions.copyCell);
      }

      if (
        typeof cell === typeof cellCopied &&
        cellCopied !== false &&
        (cellCopied.length > 0 || cellCopied !== undefined) &&
        cell !== false
      ) {
        options.push(AvailableOptions.pasteCell);
      }
    }

    setContextMenuOptions(options);
  };

  const pasteCell = () => {
    if (cellCopied === false || cellCopied.length === 0) return;

    let modifiedEngine = { ...engine };
    let change = { [cellKey]: cellCopied };

    modifiedEngine = {
      ...engine,
      ...change,
    };

    setEngine(modifiedEngine);
  };

  return (
    <>
      <Modal
        style={
          !isContextMenuEnabled
            ? { filter: "blur(3px)", zIndex: "1055" }
            : { zIndex: "1055" }
        }
        show={show}
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <Modal.Title style={{ display: "flex", marginRight: "auto" }}>
              {disabled ? "View Engine" : "Edit Engine"}
            </Modal.Title>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-bottom">
                  Toggles the <strong>axle</strong> input fields.
                </Tooltip>
              }
              delay={{ show: 250, hide: 150 }}
            >
              <button
                type="button"
                className="btn btn-outline-primary"
                style={{ display: "flex", marginRight: "10px" }}
                onClick={toggleAxles}
              >
                <ThreeDots
                  style={{
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    fontSize: "22px",
                  }}
                />
              </button>
            </OverlayTrigger>
          </div>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 1000, overflowY: "auto" }}>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Hybrid Type
            </InputGroup.Text>
            <div
              onContextMenu={(e) =>
                handleContextMenu(e, engine.hybridTypeId, "hybridTypeId")
              }
            >
              <ModelInfoSelectObject
                name="HybridType"
                attributes={hybridTypes}
                attributeId="id"
                attributeField="hybridType"
                value={engine.hybridTypeId ? engine.hybridTypeId : 0}
                alternativeValue="Unknown"
                initialValue={vehicle.initialValue.engine?.hybridTypeId}
                currentValue={engine.hybridTypeId}
                isNumbersOnly={true}
                isOptional={false}
                disabled={disabled}
                onChange={(e) => updateEngine("hybridTypeId", e)}
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Cylinder Count
            </InputGroup.Text>
            <ModelInfoInputField
              isInModal={true}
              columnName="cylinderCount"
              initialValue={vehicle.initialValue.engine.cylinderCount?.toString()}
              currentValue={engine.cylinderCount?.toString()}
              onlyAllowNumbers={true}
              isRequired={false}
              disabled={disabled}
              onChange={(e) =>
                updateEngine("cylinderCount", e === "" ? null : parseInt(e))
              }
            />
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Engine Size
            </InputGroup.Text>
            <ModelInfoInputField
              isInModal={true}
              columnName="engineSize"
              initialValue={vehicle.initialValue.engine.engineSize?.toString()}
              currentValue={engine.engineSize?.toString()}
              onlyAllowNumbers={true}
              isRequired={false}
              disabled={disabled}
              onChange={(e) =>
                updateEngine("engineSize", e === "" ? null : parseInt(e))
              }
            />
          </InputGroup>
          <InputGroup
            className="my-3 mb-0"
            size="sm"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Engine Codes
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(e, engine.engineCodes, "engineCodes");
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="engineCodes"
                initialValues={vehicle.initialValue.engine.engineCodes}
                currentValues={engine.engineCodes}
                onlyAllowNumbers={false}
                allowDecimals={false}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeStringArray={(e) => updateEngine("engineCodes", e)}
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Drivetrain Maximum Kws
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="tooltip-warning">
                    Don't forget to fill in a value when{" "}
                    <strong>possible</strong>.
                  </Tooltip>
                }
                delay={{ show: 250, hide: 150 }}
              >
                <button
                  type="button"
                  className="btn btn-danger rounded-circle"
                  style={{
                    display: "flex",
                    padding: "2px",
                    marginLeft: "10px",
                  }}
                >
                  <InfoCircle
                    style={{ fontSize: "14px", verticalAlign: "middle" }}
                  />
                </button>
              </OverlayTrigger>
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.drivetrainMaximumKws,
                    "drivetrainMaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="drivetrainMaximumKws"
                initialValues={
                  vehicle.initialValue.engine.drivetrainMaximumKws ?? []
                }
                currentValues={engine.drivetrainMaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("drivetrainMaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Drivetrain Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.drivetrainNominalKws,
                    "drivetrainNominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="drivetrainNominalKws"
                initialValues={
                  vehicle.initialValue.engine.drivetrainNominalKws ?? []
                }
                currentValues={engine.drivetrainNominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("drivetrainNominalKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Engine Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.engineMaximumKws,
                    "engineMaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="engineMaximumKws"
                initialValues={
                  vehicle.initialValue.engine.engineMaximumKws ?? []
                }
                currentValues={engine.engineMaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) => updateEngine("engineMaximumKws", e)}
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Engine Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.engineNominalKws,
                    "engineNominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="engineNominalKws"
                initialValues={
                  vehicle.initialValue.engine.engineNominalKws ?? []
                }
                currentValues={engine.engineNominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) => updateEngine("engineNominalKws", e)}
              />
            </div>
          </InputGroup>
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Front Axle 1 Maximum Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineFrontAxle1MaximumKws,
                      "engineFrontAxle1MaximumKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineFrontAxle1MaximumKws"
                  initialValues={
                    vehicle.initialValue.engine.engineFrontAxle1MaximumKws ?? []
                  }
                  currentValues={engine.engineFrontAxle1MaximumKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineFrontAxle1MaximumKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Front Axle 1 Nominal Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineFrontAxle1NominalKws,
                      "engineFrontAxle1NominalKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineFrontAxle1NominalKws"
                  initialValues={
                    vehicle.initialValue.engine.engineFrontAxle1NominalKws ?? []
                  }
                  currentValues={engine.engineFrontAxle1NominalKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineFrontAxle1NominalKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Front Axle 2 Maximum Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineFrontAxle2MaximumKws,
                      "engineFrontAxle2MaximumKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineFrontAxle2MaximumKws"
                  initialValues={
                    vehicle.initialValue.engine.engineFrontAxle2MaximumKws ?? []
                  }
                  currentValues={engine.engineFrontAxle2MaximumKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineFrontAxle2MaximumKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Front Axle 2 Nominal Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineFrontAxle2NominalKws,
                      "engineFrontAxle2NominalKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineFrontAxle2NominalKws"
                  initialValues={
                    vehicle.initialValue.engine.engineFrontAxle2NominalKws ?? []
                  }
                  currentValues={engine.engineFrontAxle2NominalKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineFrontAxle2NominalKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Rear Axle 1 Maximum Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineRearAxle1MaximumKws,
                      "engineRearAxle1MaximumKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineRearAxle1MaximumKws"
                  initialValues={
                    vehicle.initialValue.engine.engineRearAxle1MaximumKws ?? []
                  }
                  currentValues={engine.engineRearAxle1MaximumKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineRearAxle1MaximumKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Rear Axle 1 Nominal Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineRearAxle1NominalKws,
                      "engineRearAxle1NominalKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineRearAxle1NominalKws"
                  initialValues={
                    vehicle.initialValue.engine.engineRearAxle1NominalKws ?? []
                  }
                  currentValues={engine.engineRearAxle1NominalKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineRearAxle1NominalKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Rear Axle 2 Maximum Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineRearAxle2MaximumKws,
                      "engineRearAxle2MaximumKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineRearAxle2MaximumKws"
                  initialValues={
                    vehicle.initialValue.engine.engineRearAxle2MaximumKws ?? []
                  }
                  currentValues={engine.engineRearAxle2MaximumKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineRearAxle2MaximumKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          {showAxles && (
            <InputGroup
              size="sm"
              className="my-3 mb-0"
              style={{ display: "flex", flexDirection: "row" }}
            >
              <InputGroup.Text
                className={initialValueAxles ? "" : "fade-out"}
                style={{ minWidth: "300px", width: "75%" }}
              >
                Rear Axle 2 Nominal Kws
              </InputGroup.Text>
              <div
                style={{ flexGrow: 1 }}
                onContextMenu={(e) => {
                  if (isContextMenuEnabled) {
                    handleContextMenu(
                      e,
                      engine.engineRearAxle2NominalKws,
                      "engineRearAxle2NominalKws",
                    );
                  }
                }}
              >
                <ModelInfoInputFieldArray
                  columnName="engineRearAxle2NominalKws"
                  initialValues={
                    vehicle.initialValue.engine.engineRearAxle2NominalKws ?? []
                  }
                  currentValues={engine.engineRearAxle2NominalKws ?? []}
                  onlyAllowNumbers={true}
                  allowDecimals={true}
                  onShow={(show) => setIsContextMenuEnabled(!show)}
                  disabled={disabled}
                  onChangeNumberArray={(e) =>
                    updateEngine("engineRearAxle2NominalKws", e)
                  }
                />
              </div>
            </InputGroup>
          )}
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Mild Hybrid Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.mildHybridMaximumKws,
                    "mildHybridMaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="mildHybridMaximumKws"
                initialValues={
                  vehicle.initialValue.engine.mildHybridMaximumKws ?? []
                }
                currentValues={engine.mildHybridMaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("mildHybridMaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Mild Hybrid Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.mildHybridNominalKws,
                    "mildHybridNominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="mildHybridNominalKws"
                initialValues={
                  vehicle.initialValue.engine.mildHybridNominalKws ?? []
                }
                currentValues={engine.mildHybridNominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("mildHybridNominalKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 1 Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric1MaximumKws,
                    "electric1MaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric1MaximumKws"
                initialValues={
                  vehicle.initialValue.engine.electric1MaximumKws ?? []
                }
                currentValues={engine.electric1MaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric1MaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 1 Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric1NominalKws,
                    "electric1NominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric1NominalKws"
                initialValues={
                  vehicle.initialValue.engine.electric1NominalKws ?? []
                }
                currentValues={engine.electric1NominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric1NominalKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 2 Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric2MaximumKws,
                    "electric2MaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric2MaximumKws"
                initialValues={
                  vehicle.initialValue.engine.electric2MaximumKws ?? []
                }
                currentValues={engine.electric2MaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric2MaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 2 Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric2NominalKws,
                    "electric2NominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric2NominalKws"
                initialValues={
                  vehicle.initialValue.engine.electric2NominalKws ?? []
                }
                currentValues={engine.electric2NominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric2NominalKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 3 Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric3MaximumKws,
                    "electric3MaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric3MaximumKws"
                initialValues={
                  vehicle.initialValue.engine.electric3MaximumKws ?? []
                }
                currentValues={engine.electric3MaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric3MaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 3 Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric3NominalKws,
                    "electric3NominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric3NominalKws"
                initialValues={
                  vehicle.initialValue.engine.electric3NominalKws ?? []
                }
                currentValues={engine.electric3NominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric3NominalKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 4 Maximum Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric4MaximumKws,
                    "electric4MaximumKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric4MaximumKws"
                initialValues={
                  vehicle.initialValue.engine.electric4MaximumKws ?? []
                }
                currentValues={engine.electric4MaximumKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric4MaximumKws", e)
                }
              />
            </div>
          </InputGroup>
          <InputGroup
            size="sm"
            className="my-3 mb-0"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <InputGroup.Text style={{ minWidth: "300px", width: "75%" }}>
              Electric 4 Nominal Kws
            </InputGroup.Text>
            <div
              style={{ flexGrow: 1 }}
              onContextMenu={(e) => {
                if (isContextMenuEnabled) {
                  handleContextMenu(
                    e,
                    engine.electric4NominalKws,
                    "electric4NominalKws",
                  );
                }
              }}
            >
              <ModelInfoInputFieldArray
                columnName="electric4NominalKws"
                initialValues={
                  vehicle.initialValue.engine.electric4NominalKws ?? []
                }
                currentValues={engine.electric4NominalKws ?? []}
                onlyAllowNumbers={true}
                allowDecimals={true}
                onShow={(show) => setIsContextMenuEnabled(!show)}
                disabled={disabled}
                onChangeNumberArray={(e) =>
                  updateEngine("electric4NominalKws", e)
                }
              />
            </div>
          </InputGroup>
        </Modal.Body>
      </Modal>
      {isContextMenuVisible && (
        <ContextMenuEngine
          onCopyCell={() => {
            setCellCopied(contextMenuCell);
          }}
          onPasteCell={pasteCell}
          options={contextMenuOptions}
          onClose={() => setIsContextMenuVisible(false)}
          style={{
            position: "absolute",
            top: contextMenuPosition.top,
            left: contextMenuPosition.left,
            zIndex: 1060,
          }}
        />
      )}
    </>
  );
}

export default ModelInfoEngineEditModal;
